import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useState, useCallback } from "react";
import { Handle, NodeResizer, Position } from "@xyflow/react";
import { Trash2, Link2 } from "lucide-react";
import { useTranslation } from "react-i18next";
/**
 * Per-category palette. Category doubles as the note's colour and as the AI
 * signal (see ADR 0008); keep the keys in sync with `NoteCategorySchema`.
 */ const CATEGORY_STYLES = {
    context: {
        bg: "bg-muted",
        border: "border-line-strong",
        swatch: "bg-fill-neutral"
    },
    problem: {
        bg: "bg-danger-surface-strong",
        border: "border-danger-line",
        swatch: "bg-danger-solid"
    },
    question: {
        bg: "bg-warning-surface-strong",
        border: "border-warning-line",
        swatch: "bg-warning-solid"
    },
    todo: {
        bg: "bg-info-surface-strong",
        border: "border-info-line",
        swatch: "bg-info-solid"
    }
};
const CATEGORIES = [
    "context",
    "problem",
    "question",
    "todo"
];
const boxStyle = {
    width: "100%",
    height: "100%",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
    wordBreak: "break-word"
};
function ProcessFlowStickyNoteNode({ data, selected }) {
    const { t } = useTranslation();
    const [editing, setEditing] = useState(false);
    const isEdit = data?.mode === "edit";
    const commit = useCallback((e)=>{
        setEditing(false);
        const next = e.target.value;
        if (data && next !== data.text) data.onTextChange?.(data.noteId, next);
    }, [
        data
    ]);
    const enterEdit = useCallback(()=>setEditing(true), []);
    if (!data) return null;
    const style = CATEGORY_STYLES[data.category] ?? CATEGORY_STYLES.context;
    const cursor = isEdit ? "cursor-grab" : "cursor-default";
    const showControls = isEdit && !!selected;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Handle, {
                type: "source",
                position: Position.Top,
                isConnectable: false,
                style: {
                    opacity: 0,
                    pointerEvents: "none"
                }
            }),
            /*#__PURE__*/ _jsx(Handle, {
                type: "target",
                position: Position.Top,
                isConnectable: false,
                style: {
                    opacity: 0,
                    pointerEvents: "none"
                }
            }),
            isEdit && /*#__PURE__*/ _jsx(NodeResizer, {
                minWidth: 120,
                minHeight: 80,
                isVisible: !!selected,
                onResizeEnd: (_event, params)=>data.onResizeEnd?.(data.noteId, {
                        x: params.x,
                        y: params.y,
                        width: params.width,
                        height: params.height
                    })
            }),
            showControls && // `nodrag`/`nopan` so clicking controls doesn't start a node drag/pan.
            /*#__PURE__*/ _jsxs("div", {
                className: "nodrag nopan absolute -top-7 left-0 flex items-center gap-1 rounded bg-white/95 px-1 py-0.5 shadow-sm",
                children: [
                    CATEGORIES.map((cat)=>/*#__PURE__*/ _jsx("button", {
                            type: "button",
                            "aria-label": t(`processFlowEditor.noteCategory.${cat}`),
                            title: t(`processFlowEditor.noteCategory.${cat}`),
                            onClick: ()=>data.onCategoryChange?.(data.noteId, cat),
                            className: `h-3.5 w-3.5 rounded-full ${CATEGORY_STYLES[cat].swatch} ${data.category === cat ? "ring-2 ring-offset-1 ring-fill-neutral" : ""}`
                        }, cat)),
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        "aria-label": t("processFlowEditor.noteLink"),
                        title: t("processFlowEditor.noteLink"),
                        onClick: ()=>data.onToggleLink?.(data.noteId),
                        className: `ml-1 rounded p-0.5 ${data.isLinking ? "bg-info-surface-strong text-info" : "text-muted-foreground hover:text-info"}`,
                        children: /*#__PURE__*/ _jsx(Link2, {
                            className: "h-3.5 w-3.5"
                        })
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        "aria-label": t("common.delete"),
                        title: t("common.delete"),
                        onClick: ()=>data.onDelete?.(data.noteId),
                        className: "ml-0.5 text-muted-foreground hover:text-danger",
                        children: /*#__PURE__*/ _jsx(Trash2, {
                            className: "h-3.5 w-3.5"
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: `rounded-md border ${style.border} ${style.bg} p-2 text-xs text-foreground shadow-sm ${cursor}`,
                style: boxStyle,
                // Once selected, a single click drops straight into editing (click to
                // select, click again to type); double-click works from unselected too.
                onClick: isEdit && selected ? enterEdit : undefined,
                onDoubleClick: isEdit ? enterEdit : undefined,
                children: editing ? /*#__PURE__*/ _jsx("textarea", {
                    autoFocus: true,
                    defaultValue: data.text,
                    onBlur: commit,
                    className: "nodrag nopan h-full w-full resize-none border-none bg-transparent p-0 text-xs text-foreground outline-none",
                    onKeyDown: (e)=>e.stopPropagation()
                }) : data.text ? data.text : /*#__PURE__*/ _jsx("span", {
                    className: "text-fg-subtle italic",
                    children: t("processFlowEditor.emptyNote")
                })
            })
        ]
    });
}
export default /*#__PURE__*/ memo(ProcessFlowStickyNoteNode);

//# sourceMappingURL=StickyNoteNode.js.map